import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Form, Modal, Alert, Spinner, Badge, Accordion } from "react-bootstrap";
import { useEngineeringTasks } from "../../functions/useEngineeringTasks";
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { useDispatch, useSelector } from "react-redux";
import { setFilter, resetFilters, setSortBy, toggleSortOrder } from "../../redux/slices/engineeringTasksSlice";
import { selectUserDetails } from "../../redux/slices/userSlice";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { fetchData, postData, patchData } from "../../redux/slices/apiCallSlice";
import { format } from "date-fns";
import useUserDropdowns from "../../functions/useUserDropdowns";
import { formatDateForForm } from "../../utils/dateUtils";
import "./TaskManagement.css";

const TASK_PROTECTED_TIME_NOTE_PREFIX = "[TaskID:";
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WORK_WEEK_HOURS = 40;

const parseTaskCalendarDate = (value) => {
  const normalizedDate = formatDateForForm(value);
  if (!normalizedDate) return null;

  const [year, month, day] = normalizedDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTaskCalendarDate = (value) => {
  const date = parseTaskCalendarDate(value);
  return date ? format(date, "MMM dd, yyyy") : "";
};

const TaskManagement = () => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const { 
    tasks, 
    comments,
    loading, 
    error, 
    filters,
    sortBy,
    sortOrder,
    fetchTasks, 
    createTask, 
    updateTask, 
    deleteTask,
    fetchTaskComments 
  } = useEngineeringTasks({ commentsEnabled: true });

  const { mechOptions } = useUserDropdowns();
  const userDetails = useSelector(selectUserDetails);

  const assignedToOptions = useMemo(() => {
    const currentUserName = userDetails?.name?.trim();
    const currentUserEmail = userDetails?.username?.trim();

    if (!currentUserName) {
      return mechOptions;
    }

    const alreadyInOptions = mechOptions.some(
      (option) => (option?.displayName || "").toLowerCase() === currentUserName.toLowerCase()
    );

    if (alreadyInOptions) {
      return mechOptions;
    }

    return [
      ...mechOptions,
      {
        displayName: currentUserName,
        email: currentUserEmail || `current-user-${currentUserName.toLowerCase().replace(/\s+/g, "-")}`,
      },
    ];
  }, [mechOptions, userDetails]);

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedTaskForComments, setSelectedTaskForComments] = useState(null);
  const [formData, setFormData] = useState({
    Title: "",
    Description: "",
    Duration: "",
    Priority: "Low",
    Status: "Not Started",
    DueDate: "",
    DateAssigned: "",
    Effort: "",
    ShowOnSchedule: false,
    Progress: 0,
    Notes: "",
    StartDate: formatDateForForm(new Date()),
    AssignedTo: "",
    InitiatedBy: "",
    Initiative: "",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  useRealtimeDraft(showModal || showDeleteConfirm);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [groupBy, setGroupBy] = useState("none");
  const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
  const [protectedTimeError, setProtectedTimeError] = useState("");
  const [protectedTimeData, setProtectedTimeData] = useState({
    dayOfWeek: [],
    hoursPerWeek: "",
    weekPercent: "",
    notes: "",
  });

  const buildTaskProtectedTimeNote = (taskId, notes = "") => {
    const normalizedNotes = (notes || "").trim();
    const taskMarker = `${TASK_PROTECTED_TIME_NOTE_PREFIX}${taskId}]`;

    if (!normalizedNotes) {
      return taskMarker;
    }

    if (normalizedNotes.includes(taskMarker)) {
      return normalizedNotes;
    }

    return `${normalizedNotes} ${taskMarker}`;
  };

  const parseProtectedDayOfWeek = (value) => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      return value
        .split(",")
        .map((day) => day.trim())
        .filter(Boolean);
    }

    return [];
  };

  const getCalculatedProtectedStartDate = () => {
    const dueDate = formData.DueDate;
    const dateAssigned = formData.DateAssigned;
    const effortHours = parseFloat(formData.Effort);
    const explicitHoursPerWeek = parseFloat(protectedTimeData.hoursPerWeek);
    const weekPercent = parseFloat(protectedTimeData.weekPercent);

    let capacityHoursPerWeek = 0;
    if (!Number.isNaN(explicitHoursPerWeek) && explicitHoursPerWeek > 0) {
      capacityHoursPerWeek = explicitHoursPerWeek;
    } else if (!Number.isNaN(weekPercent) && weekPercent > 0) {
      capacityHoursPerWeek = (weekPercent / 100) * WORK_WEEK_HOURS;
    }

    // If inputs exist, derive start date from due date and expected weekly capacity.
    if (
      dueDate &&
      !Number.isNaN(effortHours) &&
      effortHours > 0 &&
      capacityHoursPerWeek > 0
    ) {
      const weeksNeeded = effortHours / capacityHoursPerWeek;
      const totalDaysNeeded = Math.max(1, Math.ceil(weeksNeeded * 7));
      const due = parseTaskCalendarDate(dueDate);

      if (due) {
        const start = new Date(due);
        start.setDate(start.getDate() - (totalDaysNeeded - 1));
        return formatDateForForm(start);
      }
    }

    return dateAssigned || formatDateForForm(new Date());
  };

  const validateProtectedTimeForm = () => {
    const hasDayOfWeek = protectedTimeData.dayOfWeek.length > 0;
    const hasHoursPerWeek = protectedTimeData.hoursPerWeek !== "";
    const hasWeekPercent = protectedTimeData.weekPercent !== "";

    if (!formData.AssignedTo) {
      setProtectedTimeError("Assigned engineer is required when Add to Schedule is selected.");
      return false;
    }

    if (!formData.DueDate) {
      setProtectedTimeError("Due Date is required when Add to Schedule is selected.");
      return false;
    }

    if (!hasDayOfWeek && !hasHoursPerWeek && !hasWeekPercent) {
      setProtectedTimeError("Provide Day(s) of Week, Hours Per Week, or Week Percent to create schedule protection.");
      return false;
    }

    setProtectedTimeError("");
    return true;
  };

  const getLinkedProtectedTimeRecord = async (taskId) => {
    const taskMarker = `${TASK_PROTECTED_TIME_NOTE_PREFIX}${taskId}]`;
    const protectedTimeRecords = await dispatch(
      fetchData({ url: apiUrls.engineeringProtectedTime })
    ).unwrap();

    if (!Array.isArray(protectedTimeRecords)) {
      return null;
    }

    return protectedTimeRecords.find(
      (record) => typeof record.Notes === "string" && record.Notes.includes(taskMarker)
    ) || null;
  };

  const loadLinkedProtectedTimeData = async (task) => {
    if (!task?.ID || !task?.ShowOnSchedule) {
      return;
    }

    try {
      const linkedRecord = await getLinkedProtectedTimeRecord(task.ID);

      if (!linkedRecord) {
        return;
      }

      setProtectedTimeData({
        dayOfWeek: parseProtectedDayOfWeek(linkedRecord.DayOfWeek),
        hoursPerWeek: linkedRecord.HoursPerWeek ?? "",
        weekPercent: linkedRecord.WeekPercent ?? "",
        notes: linkedRecord.Notes || "",
      });
    } catch (err) {
      console.error("Error loading linked protected time:", err);
    }
  };

  const upsertProtectedTimeForTask = async (task) => {
    if (!task?.ID) {
      return;
    }

    if (!validateProtectedTimeForm()) {
      throw new Error("Protected time validation failed");
    }

    const calculatedStartDate = getCalculatedProtectedStartDate();
    const taskMarkerNote = buildTaskProtectedTimeNote(task.ID, protectedTimeData.notes);
    const linkedRecord = await getLinkedProtectedTimeRecord(task.ID);

    if (linkedRecord?.ID) {
      await dispatch(
        patchData({
          url: `${apiUrls.engineeringProtectedTime}?id=${linkedRecord.ID}`,
          data: {
            ID: linkedRecord.ID,
            UserName: formData.AssignedTo,
            StartDate: calculatedStartDate || null,
            EndDate: formData.DueDate || null,
            Notes: taskMarkerNote,
            DayOfWeek: protectedTimeData.dayOfWeek.length > 0 ? protectedTimeData.dayOfWeek.join(",") : null,
            HoursPerWeek: protectedTimeData.hoursPerWeek || null,
            WeekPercent: protectedTimeData.weekPercent || null,
          },
        })
      ).unwrap();
      return;
    }

    await dispatch(
      postData({
        url: apiUrls.engineeringProtectedTime,
        data: {
          userName: formData.AssignedTo,
          startDate: calculatedStartDate || null,
          endDate: formData.DueDate || null,
          notes: taskMarkerNote,
          dayOfWeek: protectedTimeData.dayOfWeek.length > 0 ? protectedTimeData.dayOfWeek.join(",") : null,
          hoursPerWeek: protectedTimeData.hoursPerWeek || null,
          weekPercent: protectedTimeData.weekPercent || null,
        },
      })
    ).unwrap();
  };

  // Get unique initiative values for dropdown
  const uniqueInitiatives = useMemo(() => {
    const initiatives = tasks
      .map(task => task.Initiative)
      .filter(initiative => initiative && initiative.trim() !== "")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return initiatives;
  }, [tasks]);

  // Get unique InitiatedBy values for dropdown
  const uniqueInitiatedBy = useMemo(() => {
    const initiatedBy = tasks
      .map(task => task.InitiatedBy)
      .filter(name => name && name.trim() !== "")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return initiatedBy;
  }, [tasks]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Filter and sort tasks
  const filteredAndSortedTasks = React.useMemo(() => {
    let filtered = [...tasks];

    // Apply filters
    if (!filters.showCompleted) {
      filtered = filtered.filter(task => task.Status !== "Completed");
    }

    if (filters.assignedTo) {
      filtered = filtered.filter(task => task.AssignedTo === filters.assignedTo);
    }

    if (filters.priority) {
      filtered = filtered.filter(task => task.Priority === filters.priority);
    }

    if (filters.status) {
      filtered = filtered.filter(task => task.Status === filters.status);
    }

    if (filters.initiatedBy) {
      filtered = filtered.filter(task => task.InitiatedBy === filters.initiatedBy);
    }

    if (filters.initiative) {
      filtered = filtered.filter(task => task.Initiative === filters.initiative);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle null/undefined values
      if (!aVal && !bVal) return 0;
      if (!aVal) return sortOrder === "asc" ? 1 : -1;
      if (!bVal) return sortOrder === "asc" ? -1 : 1;

      // Handle dates
      if (sortBy === "DueDate" || sortBy === "StartDate") {
        aVal = formatDateForForm(aVal);
        bVal = formatDateForForm(bVal);
      } else if (sortBy === "DateCreated") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numbers
      if (sortBy === "Priority") {
        const priorityOrder = { "Low": 1, "Medium": 2, "High": 3, "Critical": 4 };
        aVal = priorityOrder[aVal] || 0;
        bVal = priorityOrder[bVal] || 0;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [tasks, filters, sortBy, sortOrder]);

  // Group tasks by selected field
  const groupedTasks = useMemo(() => {
    if (groupBy === "none") {
      return { "All Tasks": filteredAndSortedTasks };
    }

    const groups = {};
    filteredAndSortedTasks.forEach(task => {
      let groupKey = task[groupBy];
      
      // Handle empty/null values
      if (!groupKey || groupKey === "") {
        groupKey = "Unassigned";
      }
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(task);
    });

    // Sort group keys
    const sortedGroups = {};
    Object.keys(groups).sort().forEach(key => {
      sortedGroups[key] = groups[key];
    });

    return sortedGroups;
  }, [filteredAndSortedTasks, groupBy]);

  // Handle accordion toggle
  const handleAccordionToggle = (key) => {
    setActiveAccordionKeys(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleOpenModal = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        Title: task.Title || "",
        Description: task.Description || "",
        Duration: task.Duration || "",
        Priority: task.Priority || "Medium",
        Status: task.Status || "Not Started",
        DueDate: formatDateForForm(task.DueDate),
        DateAssigned: formatDateForForm(task.DateAssigned),
        Effort: task.Effort || "",
        ShowOnSchedule: task.ShowOnSchedule || false,
        Progress: task.Progress || 0,
        Notes: task.Notes || "",
        StartDate: formatDateForForm(task.StartDate),
        AssignedTo: task.AssignedTo || "",
        InitiatedBy: task.InitiatedBy || "",
        Initiative: task.Initiative || "",
      });
      setProtectedTimeData({
        dayOfWeek: [],
        hoursPerWeek: task.Effort || "",
        weekPercent: "",
        notes: "",
      });

      if (task.ShowOnSchedule) {
        loadLinkedProtectedTimeData(task);
      }
    } else {
      setEditingTask(null);
      setFormData({
        Title: "",
        Description: "",
        Duration: "",
        Priority: "Low",
        Status: "Not Started",
        DueDate: "",
        DateAssigned: formatDateForForm(new Date()),
        Effort: "",
        ShowOnSchedule: false,
        Progress: 0,
        Notes: "",
        StartDate: formatDateForForm(new Date()),
        AssignedTo: "",
        InitiatedBy: "",
        Initiative: "",
      });
      setProtectedTimeData({
        dayOfWeek: [],
        hoursPerWeek: "",
        weekPercent: "",
        notes: "",
      });
    }
    setProtectedTimeError("");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTask(null);
    setProtectedTimeError("");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));

    if (name === "ShowOnSchedule" && !checked) {
      setProtectedTimeError("");
    }
  };

  const handleProtectedTimeChange = (e) => {
    const { name, value } = e.target;
    setProtectedTimeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProtectedDayToggle = (day) => {
    setProtectedTimeData((prev) => {
      const currentDays = prev.dayOfWeek || [];
      if (currentDays.includes(day)) {
        return {
          ...prev,
          dayOfWeek: currentDays.filter((d) => d !== day),
        };
      }
      return {
        ...prev,
        dayOfWeek: [...currentDays, day],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let savedTask = null;

      if (editingTask) {
        // PUT/PATCH uses PascalCase with ID in body
        const updateData = {
          ID: editingTask.ID,
          Title: formData.Title,
          Description: formData.Description || null,
          Duration: formData.Duration ? parseFloat(formData.Duration) : null,
          Priority: formData.Priority,
          Status: formData.Status,
          DueDate: formData.DueDate || null,
          DateAssigned: formData.DateAssigned || null,
          Effort: formData.Effort ? parseFloat(formData.Effort) : null,
          ShowOnSchedule: formData.ShowOnSchedule || null,
          Progress: formData.Progress ? String(formData.Progress) : null,
          Notes: formData.Notes || null,
          StartDate: formData.StartDate || null,
          AssignedTo: formData.AssignedTo || null,
          InitiatedBy: formData.InitiatedBy || null,
          Initiative: formData.Initiative || null,
        };
        savedTask = await updateTask(editingTask.ID, updateData);
      } else {
        // POST uses camelCase
        const createData = {
          title: formData.Title,
          description: formData.Description || null,
          duration: formData.Duration ? parseFloat(formData.Duration) : null,
          priority: formData.Priority,
          status: formData.Status,
          dueDate: formData.DueDate || null,
          dateAssigned: formData.DateAssigned || null,
          effort: formData.Effort ? parseFloat(formData.Effort) : null,
          showOnSchedule: formData.ShowOnSchedule || null,
          progress: formData.Progress ? String(formData.Progress) : null,
          notes: formData.Notes || null,
          startDate: formData.StartDate || null,
          assignedTo: formData.AssignedTo || null,
          initiatedBy: formData.InitiatedBy || null,
          initiative: formData.Initiative || null,
        };
        savedTask = await createTask(createData);
      }

      if (formData.ShowOnSchedule) {
        await upsertProtectedTimeForTask(savedTask || editingTask);
      }

      handleCloseModal();
      fetchTasks();
    } catch (err) {
      console.error("Error saving task:", err);
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTask(taskToDelete.ID);
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (err) {
      console.error("Error deleting task:", err);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      dispatch(toggleSortOrder());
    } else {
      dispatch(setSortBy(field));
    }
  };

  const getPriorityBadge = (priority) => {
    const { PRIORITY_BADGE_VARIANTS } = require('../../utils/priorityBadgeConfig');
    return <Badge bg={PRIORITY_BADGE_VARIANTS[priority] || "secondary"}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      "Not Started": "secondary",
      "In Progress": "primary",
      "On Hold": "warning",
      "Completed": "success",
      "Cancelled": "danger"
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const handleViewComments = async (task) => {
    setSelectedTaskForComments(task);
    await fetchTaskComments(task.ID);
    setShowCommentsModal(true);
  };

  const handleCloseCommentsModal = () => {
    setShowCommentsModal(false);
    setSelectedTaskForComments(null);
  };

  const getCommentCount = (taskId) => {
    if (!comments || !comments[taskId]) return 0;
    return comments[taskId].filter(c => c.TaskID === taskId).length;
  };

  // Render a single task row
  const renderTaskRow = (task) => {
    const commentCount = getCommentCount(task.ID);
    
    return (
    <tr key={task.ID}>
      <td>
        <div className="d-flex align-items-start gap-2">
          <div className="flex-grow-1">
            <strong>{task.Title}</strong>
            {task.Description && (
              <div className="text-muted small">{task.Description}</div>
            )}
          </div>
          {commentCount > 0 && (
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => handleViewComments(task)}
              title={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
            >
              <i className="bi bi-chat-left-text"></i> {commentCount}
            </Button>
          )}
        </div>
      </td>
      <td>{task.AssignedTo || "-"}</td>
      <td>{task.InitiatedBy || "-"}</td>
      <td>{task.Initiative || "-"}</td>
      <td>{getPriorityBadge(task.Priority)}</td>
      <td>{getStatusBadge(task.Status)}</td>
      <td>
        {formatTaskCalendarDate(task.DueDate) || "-"}
      </td>
      <td>{task.Progress ? `${task.Progress}%` : "0%"}</td>
      <td>
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          onClick={() => handleOpenModal(task)}
        >
          Edit
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDeleteClick(task)}
        >
          Delete
        </Button>
      </td>
    </tr>
    );
  };

  // Render table with or without grouping
  const renderTasksTable = () => {
    if (groupBy === "none") {
      // Render single table without grouping
      return (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th onClick={() => handleSort("Title")} style={{ cursor: "pointer" }}>
                  Title {sortBy === "Title" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("AssignedTo")} style={{ cursor: "pointer" }}>
                  Assigned To {sortBy === "AssignedTo" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("InitiatedBy")} style={{ cursor: "pointer" }}>
                  Initiated By {sortBy === "InitiatedBy" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("Initiative")} style={{ cursor: "pointer" }}>
                  Initiative {sortBy === "Initiative" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("Priority")} style={{ cursor: "pointer" }}>
                  Priority {sortBy === "Priority" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("Status")} style={{ cursor: "pointer" }}>
                  Status {sortBy === "Status" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th onClick={() => handleSort("DueDate")} style={{ cursor: "pointer" }}>
                  Due Date {sortBy === "DueDate" && (sortOrder === "asc" ? "↑" : "↓")}
                </th>
                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedTasks.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">No tasks found</td>
                </tr>
              ) : (
                filteredAndSortedTasks.map(task => renderTaskRow(task))
              )}
            </tbody>
          </Table>
        </div>
      );
    }

    // Render grouped accordion view
    return (
      <Accordion activeKey={activeAccordionKeys} alwaysOpen>
        {Object.entries(groupedTasks).map(([groupKey, groupTasks]) => (
          <Accordion.Item eventKey={groupKey} key={groupKey}>
            <Accordion.Header onClick={() => handleAccordionToggle(groupKey)}>
              <strong>{groupKey}</strong>
              <Badge bg="secondary" className="ms-2">{groupTasks.length}</Badge>
            </Accordion.Header>
            <Accordion.Body>
              <div className="table-responsive">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("Title")} style={{ cursor: "pointer" }}>
                        Title {sortBy === "Title" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => handleSort("AssignedTo")} style={{ cursor: "pointer" }}>
                        Assigned To {sortBy === "AssignedTo" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => handleSort("InitiatedBy")} style={{ cursor: "pointer" }}>
                        Initiated By {sortBy === "InitiatedBy" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => handleSort("Initiative")} style={{ cursor: "pointer" }}>
                        Initiative {sortBy === "Initiative" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => handleSort("Priority")} style={{ cursor: "pointer" }}>
                        Priority {sortBy === "Priority" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => handleSort("Status")} style={{ cursor: "pointer" }}>
                        Status {sortBy === "Status" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th onClick={() => handleSort("DueDate")} style={{ cursor: "pointer" }}>
                        Due Date {sortBy === "DueDate" && (sortOrder === "asc" ? "↑" : "↓")}
                      </th>
                      <th>Progress</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupTasks.map(task => renderTaskRow(task))}
                  </tbody>
                </Table>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>
    );
  };

  return (
    <div className="task-management-container">
      <div className="task-header">
        <h2>Engineering Task Management</h2>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <i className="bi bi-plus-circle"></i> Create New Task
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="task-filters">
        <Form.Group className="filter-group">
          <Form.Label>Assigned To</Form.Label>
          <Form.Select
            value={filters.assignedTo || ""}
            onChange={(e) => dispatch(setFilter({ key: "assignedTo", value: e.target.value || null }))}
          >
            <option value="">All Engineers</option>
            {assignedToOptions.map((engineer) => (
              <option key={engineer.email || engineer.displayName} value={engineer.displayName}>
                {engineer.displayName}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Priority</Form.Label>
          <Form.Select
            value={filters.priority || ""}
            onChange={(e) => dispatch(setFilter({ key: "priority", value: e.target.value || null }))}
          >
            <option value="">All Priorities</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Status</Form.Label>
          <Form.Select
            value={filters.status || ""}
            onChange={(e) => dispatch(setFilter({ key: "status", value: e.target.value || null }))}
          >
            <option value="">All Statuses</option>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="On Hold">On Hold</option>
            <option value="Completed">Completed</option>
            <option value="Cancelled">Cancelled</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Check
            type="checkbox"
            label="Show Completed Tasks"
            checked={filters.showCompleted}
            onChange={(e) => dispatch(setFilter({ key: "showCompleted", value: e.target.checked }))}
          />
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Initiated By</Form.Label>
          <Form.Select
            value={filters.initiatedBy || ""}
            onChange={(e) => dispatch(setFilter({ key: "initiatedBy", value: e.target.value || null }))}
          >
            <option value="">All</option>
            {uniqueInitiatedBy.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Initiative</Form.Label>
          <Form.Select
            value={filters.initiative || ""}
            onChange={(e) => dispatch(setFilter({ key: "initiative", value: e.target.value || null }))}
          >
            <option value="">All Initiatives</option>
            {uniqueInitiatives.map((initiative) => (
              <option key={initiative} value={initiative}>
                {initiative}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Group By</Form.Label>
          <Form.Select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            <option value="none">No Grouping</option>
            <option value="AssignedTo">Assigned To</option>
            <option value="InitiatedBy">Initiated By</option>
            <option value="Initiative">Initiative</option>
            <option value="Priority">Priority</option>
            <option value="Status">Status</option>
          </Form.Select>
        </Form.Group>

        <Button variant="outline-secondary" onClick={() => dispatch(resetFilters())}>
          Reset Filters
        </Button>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        renderTasksTable()
      )}

      {/* Create/Edit Task Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingTask ? "Edit Task" : "Create New Task"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="Title"
                value={formData.Title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Assigned To</Form.Label>
                  <Form.Select
                    name="AssignedTo"
                    value={formData.AssignedTo}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Engineer...</option>
                      {assignedToOptions.map((engineer) => (
                        <option key={engineer.email || engineer.displayName} value={engineer.displayName}>
                        {engineer.displayName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="Priority"
                    value={formData.Priority}
                    onChange={handleInputChange}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Initiated By</Form.Label>
                  <Form.Control
                    type="text"
                    name="InitiatedBy"
                    value={formData.InitiatedBy}
                    onChange={handleInputChange}
                    placeholder="Enter name..."
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Initiative</Form.Label>
                  <Form.Control
                    type="text"
                    name="Initiative"
                    value={formData.Initiative}
                    onChange={handleInputChange}
                    list="initiative-options"
                    placeholder="Enter initiative or select from existing..."
                  />
                  <datalist id="initiative-options">
                    {uniqueInitiatives.map((initiative) => (
                      <option key={initiative} value={initiative} />
                    ))}
                  </datalist>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="Status"
                    value={formData.Status}
                    onChange={handleInputChange}
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Progress (%)</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Range
                      name="Progress"
                      min="0"
                      max="100"
                      step="5"
                      value={formData.Progress}
                      onChange={handleInputChange}
                    />
                    <span style={{ minWidth: "50px" }}>{formData.Progress}%</span>
                  </div>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="DateAssigned"
                    value={formData.DateAssigned}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="DueDate"
                    value={formData.DueDate}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Effort (hours)</Form.Label>
              <Form.Control
                type="number"
                name="Effort"
                step="0.5"
                value={formData.Effort}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="ShowOnSchedule"
                label="Show on Schedule"
                checked={formData.ShowOnSchedule}
                onChange={handleInputChange}
              />
            </Form.Group>

            {formData.ShowOnSchedule && (
              <>
                <hr />
                <h6 className="mb-3">Protected Time (Created with Task)</h6>
                {protectedTimeError && (
                  <Alert variant="danger">{protectedTimeError}</Alert>
                )}

                <Alert variant="light" className="mb-3 border">
                  <div><strong>Engineer:</strong> {formData.AssignedTo || "(set Assigned To above)"}</div>
                  <div><strong>Protected End Date:</strong> {formData.DueDate || "(set Due Date above)"}</div>
                  <div><strong>Calculated Protected Start Date:</strong> {getCalculatedProtectedStartDate()}</div>
                </Alert>

                <Form.Group className="mb-3">
                  <Form.Label>Day(s) of Week</Form.Label>
                  <div>
                    {DAYS_OF_WEEK.map((day) => (
                      <Form.Check
                        key={day}
                        inline
                        type="checkbox"
                        label={day}
                        checked={protectedTimeData.dayOfWeek.includes(day)}
                        onChange={() => handleProtectedDayToggle(day)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Hours Per Week</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.5"
                        name="hoursPerWeek"
                        value={protectedTimeData.hoursPerWeek}
                        onChange={handleProtectedTimeChange}
                        placeholder="e.g., 8"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Week Percent</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        name="weekPercent"
                        value={protectedTimeData.weekPercent}
                        onChange={handleProtectedTimeChange}
                        placeholder="e.g., 25"
                      />
                    </Form.Group>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Protected Time Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="notes"
                    value={protectedTimeData.notes}
                    onChange={handleProtectedTimeChange}
                    placeholder="Optional context for this protected time"
                  />
                </Form.Group>

                <Alert variant="info" className="mb-0">
                  At least one of Day(s) of Week, Hours Per Week, or Week Percent is required.
                </Alert>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingTask ? "Update Task" : "Create Task"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the task "{taskToDelete?.Title}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Comments Modal */}
      <Modal show={showCommentsModal} onHide={handleCloseCommentsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-chat-left-text"></i> Comments - {selectedTaskForComments?.Title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTaskForComments && (
            <div>
              <div className="mb-3 p-3 bg-light rounded">
                <div><strong>Task:</strong> {selectedTaskForComments.Title}</div>
                {selectedTaskForComments.Description && (
                  <div className="mt-2"><strong>Description:</strong> {selectedTaskForComments.Description}</div>
                )}
                <div className="mt-2">
                  <strong>Assigned To:</strong> {selectedTaskForComments.AssignedTo || "Unassigned"} • 
                  <strong className="ms-2">Initiated By:</strong> {selectedTaskForComments.InitiatedBy || "Not specified"} • 
                  <strong className="ms-2">Initiative:</strong> {selectedTaskForComments.Initiative || "Not specified"}
                </div>
                <div className="mt-2">
                  <strong>Status:</strong> {getStatusBadge(selectedTaskForComments.Status)} • 
                  <strong className="ms-2">Priority:</strong> {getPriorityBadge(selectedTaskForComments.Priority)}
                </div>
              </div>

              <h6 className="mb-3">
                Comments 
                {comments && comments[selectedTaskForComments.ID] && (
                  <Badge bg="secondary" className="ms-2">
                    {comments[selectedTaskForComments.ID].filter(c => c.TaskID === selectedTaskForComments.ID).length}
                  </Badge>
                )}
              </h6>

              <div className="comments-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                {comments && comments[selectedTaskForComments.ID] && comments[selectedTaskForComments.ID].length > 0 ? (
                  comments[selectedTaskForComments.ID]
                    .filter(comment => comment.TaskID === selectedTaskForComments.ID)
                    .map((comment) => (
                      <div key={comment.ID} className="comment-item mb-2 p-3 bg-light rounded">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <small className="text-muted">
                              <strong>{comment.Author}</strong> • {format(new Date(comment.DateCreated), "MMM dd, yyyy 'at' h:mm a")}
                            </small>
                            <p className="mb-0 mt-2">{comment.Comment}</p>
                          </div>
                        </div>
                      </div>
                    ))
                ) : (
                  <p className="text-muted">No comments for this task.</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCommentsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TaskManagement;
